import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../app/authContext";
import {
  approveRun,
  rejectRun,
  ReportApiError,
  type ReportDocument,
  type ReportErrorCode,
} from "../../services/report";
import { signatureLine } from "./ReportSignatures";

// The OWNER's approval actions on the report page (CTO decision D8, 2026-10-14; the commit 5
// backend action, A31 (d), D5): Approve and Reject, shown to an OWNER only and only on a run
// that is not approved. Approve confirms with the run's content hash and a preview of the D7
// signature line; Reject asks for a reason, required and trimmed, before anything is sent.
// After a success the report is loaded again through the commit 6 client - the query is
// invalidated, nothing is patched locally - so the DRAFT watermark goes with the server's own
// answer. A refusal is shown in its own words and nothing is retried.

const LABEL = /^.+ \(([^\s()]+@[^\s()]+)\)$/;

/** The e-mail inside a record label "Name (email)", or null for free text; never a name. */
export function emailOfLabel(label: string | null): string | null {
  if (label === null) {
    return null;
  }
  const match = LABEL.exec(label);
  return match === null ? null : match[1]!.toLowerCase();
}

export const APPROVAL_ERROR_MESSAGES: Partial<Record<ReportErrorCode, string>> = {
  ALREADY_APPROVED: "This run was already approved; the page shows the record as stored.",
  ALREADY_REJECTED: "This run was already rejected; the page shows the record as stored.",
  REVISION_ISSUED:
    "The project revision is issued; its runs can no longer be approved or rejected.",
  EVIDENCE_CHECKSUM_MISMATCH:
    "The stored evidence does not match the hash stored with the run; it was not approved. " +
    "The evidence needs review.",
  FORBIDDEN: "Only an OWNER may approve or reject a run.",
  UNAUTHENTICATED: "Your session has ended. Sign in again to approve or reject this run.",
  REASON_REQUIRED: "A reason is required.",
};

/** The sentence shown for a refused action, with the code a test or a style can read. */
export function describeApprovalFailure(error: unknown): { code: string; message: string } {
  if (error instanceof ReportApiError) {
    return {
      code: error.code,
      message: APPROVAL_ERROR_MESSAGES[error.code] ?? `The action was refused: ${error.message}`,
    };
  }
  const detail = error instanceof Error && error.message.trim() !== "" ? ` ${error.message}` : "";
  return { code: "UNKNOWN", message: `The action failed.${detail}` };
}

type Mode = "idle" | "approve" | "reject";

type ReportApprovalActionsProps = {
  document: ReportDocument;
};

export function ReportApprovalActions({ document }: ReportApprovalActionsProps) {
  const { state } = useAuth();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>("idle");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<{ code: string; message: string } | null>(null);

  async function reload(): Promise<void> {
    setMode("idle");
    setNotes("");
    setReason("");
    setProblem(null);
    await queryClient.invalidateQueries({ queryKey: ["report"] });
  }

  const approval = useMutation({
    mutationFn: (text: string) => approveRun(document.run_id, text === "" ? undefined : text),
    onSuccess: reload,
    onError: (error) => setProblem(describeApprovalFailure(error)),
  });
  const rejection = useMutation({
    mutationFn: (text: string) => rejectRun(document.run_id, text),
    onSuccess: reload,
    onError: (error) => setProblem(describeApprovalFailure(error)),
  });

  const session = state.status === "signed-in" ? state.session : null;
  const approved = document.status.approval_status === "APPROVED";
  if (session === null || session.role !== "OWNER" || approved) {
    return null;
  }

  const rejected = document.status.approval_status === "REJECTED";
  const ownerLabel = `${session.user.full_name} (${session.user.email})`;
  const preparerEmail = emailOfLabel(document.signatures.prepared_by);
  // The preview of the D7 line the server will derive: the preparer's e-mail (an identity key,
  // read from the record label as the backend reads it) against the signed-in owner's.
  const selfChecked = preparerEmail === null ? null : preparerEmail === session.user.email.toLowerCase();
  const preview = signatureLine(
    { ...document.signatures, approved_by_name: ownerLabel, self_checked: selfChecked },
    true,
  );
  const busy = approval.isPending || rejection.isPending;

  function open(next: Mode): void {
    setProblem(null);
    setMode(next);
  }

  function confirmRejection(): void {
    const trimmed = reason.trim();
    if (trimmed === "") {
      setProblem({ code: "REASON_REQUIRED", message: APPROVAL_ERROR_MESSAGES.REASON_REQUIRED! });
      return;
    }
    rejection.mutate(trimmed);
  }

  return (
    <section aria-label="Approval" data-report-approval data-report-approval-mode={mode}>
      <h3>Approval</h3>
      <p>
        This run is {rejected ? "rejected" : "not yet approved"}. An approval records your user
        id, the time and the run's content hash; the report then carries no DRAFT mark.
      </p>
      <p data-report-approval-buttons>
        <button type="button" onClick={() => open("approve")} disabled={busy || mode !== "idle"}>
          Approve
        </button>
        {rejected ? null : (
          <button type="button" onClick={() => open("reject")} disabled={busy || mode !== "idle"}>
            Reject
          </button>
        )}
      </p>

      {mode === "approve" ? (
        <div role="dialog" aria-label="Approve this run" data-report-approval-dialog="approve">
          <p>
            Content hash (SHA-256) <code>{document.content_hash}</code>
          </p>
          <p data-report-approval-preview>
            <strong>{preview}</strong>
          </p>
          <label>
            Notes (optional)
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              maxLength={2000}
              disabled={busy}
            />
          </label>
          {problem ? (
            <p role="alert" data-report-approval-error={problem.code}>
              {problem.message}
            </p>
          ) : null}
          <p>
            <button type="button" onClick={() => approval.mutate(notes.trim())} disabled={busy}>
              Confirm approval
            </button>
            <button type="button" onClick={() => open("idle")} disabled={busy}>
              Cancel
            </button>
          </p>
        </div>
      ) : null}

      {mode === "reject" ? (
        <div role="dialog" aria-label="Reject this run" data-report-approval-dialog="reject">
          <label>
            Reason (required)
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              maxLength={2000}
              disabled={busy}
            />
          </label>
          {problem ? (
            <p role="alert" data-report-approval-error={problem.code}>
              {problem.message}
            </p>
          ) : null}
          <p>
            <button type="button" onClick={confirmRejection} disabled={busy}>
              Confirm rejection
            </button>
            <button type="button" onClick={() => open("idle")} disabled={busy}>
              Cancel
            </button>
          </p>
        </div>
      ) : null}
    </section>
  );
}

export default ReportApprovalActions;
